import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    TooltipHost,
} from '@fluentui/react';
import { ThemeContext } from '../../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../../Common/util/localStorageUtils';
import { useSreAccess } from '../../../../../Common/hooks/useSreAccess';
import {
    useSessionState,
    useUrlParams,
} from '../../../../../Common/hooks/useUrlState';
import { AutoRefresh } from '../../../../../AGSS/components/MultistageReleaseData/AutoRefresh';
import {
    SreEntityType,
    SreInflightSearchQuery,
    SreSortDirection,
    supportsFindings,
    SRE_DEFAULT_PAGE_SIZE,
} from '../../../../../AGSS/models/SreHealth';
import { CopyButton } from './CopyButton';
import { useFindingsExport } from './useFindingsExport';
import { sreCacheScope } from './sreSessionCache';
import { useSreTriageQuery } from './useSreTriageQuery';
import { useSreProgress } from './useSreProgress';
import { SreTriageFilters, hasActiveFilters } from './SreTriageFilters';
import { SreTriageSummary } from './SreTriageSummary';
import { SreStageBreakdown } from './SreStageBreakdown';
import { SreTriageList } from './SreTriageList';
import { SreTriageDetailPanel } from './SreTriageDetailPanel';
import {
    buildInflightCsv,
    downloadCsv,
    inflightCsvFileName,
} from './csvExport';
import { formatRelativeToNow } from './formatting';

interface ISreTriageProps {}

const TABS: { key: SreEntityType; text: string }[] = [
    { key: 'build', text: 'Builds' },
    { key: 'deployment', text: 'Deployments' },
];

const defaultSortFor = (type: SreEntityType): string =>
    type === 'build' ? 'stagedInLowshare' : 'writtenTime';

/**
 * Combined SRE triage view: filter, page and drill into in-flight orchestrations.
 *
 * Build and deployment are separate tabs rather than one merged list, because their columns differ
 * and their stage numbers collide (stage 4 is ValidateBuild for builds and Undefined for
 * deployments). They are also separate endpoints over separate tables, so a merged list could not
 * page correctly server-side.
 */
export const SreTriage: React.FC<ISreTriageProps> = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const access = useSreAccess();
    const params = useUrlParams();

    // Query state lives on the URL so it survives switching admin cards and can be shared.
    const type: SreEntityType =
        params.getString('sreTab') === 'deployment' ? 'deployment' : 'build';
    const prefix = type === 'build' ? 'b' : 'd';

    const query: SreInflightSearchQuery = useMemo(
        () => ({
            search: params.getString(`${prefix}Search`),
            uniqueIdentifier: params.getString(`${prefix}Id`),
            stage: params.getNumberArray(`${prefix}Stage`),
            stageState: params.getNumberArray(`${prefix}State`),
            orchestrationType: params.getString(`${prefix}Type`),
            pipeline: params.getString(`${prefix}Pipeline`),
            region: params.getString(`${prefix}Region`),
            agedOlderThanHours: params.getNumber(`${prefix}Aged`),
            minRetryCount: params.getNumber(`${prefix}Retries`),
            sortBy: params.getString(`${prefix}Sort`) ?? defaultSortFor(type),
            sortDirection:
                (params.getString(`${prefix}Dir`) as SreSortDirection) ?? 'asc',
            page: params.getNumber(`${prefix}Page`) ?? 1,
            pageSize:
                params.getNumber(`${prefix}Size`) ?? SRE_DEFAULT_PAGE_SIZE,
        }),
        [params, prefix, type]
    );

    const [expandedByTab, setExpandedByTab] = useSessionState<
        Record<string, string[]>
    >(`sreTriageExpanded:${sreCacheScope()}`, {});
    const [breakdownOpen, setBreakdownOpen] = useSessionState<boolean>(
        'sreTriageBreakdownOpen',
        false
    );

    // Selection is deliberately NOT persisted. It is scoped to what the user can currently see, and
    // a filter change invalidates that, so it resets below rather than quietly carrying rows that
    // are no longer in the result into an export.
    const [selected, setSelected] = useState<number[]>([]);

    const expanded = expandedByTab[type] ?? [];

    const { data, isLoading, error, lastUpdated, refresh } = useSreTriageQuery(
        type,
        query,
        access.canCallApi
    );

    const progress = useSreProgress(type);

    const detailsId = params.getString('sreDetail') ?? null;
    const setDetailsId = (identifier: string | null) =>
        params.setParams({ sreDetail: identifier });
    const [lookupId, setLookupId] = useState('');
    const detailsItem =
        data?.items.find((i) => i.uniqueIdentifier === detailsId) ?? null;
    const visibleProgress = JSON.stringify(
        Array.from(
            new Set([
                ...(data?.items ?? [])
                    .filter((item) => expanded.includes(item.uniqueIdentifier))
                    .map((item) => item.uniqueIdentifier),
                ...(detailsId ? [detailsId] : []),
            ])
        )
    );
    const previousVisible = useRef<string[]>([]);
    const { load, cancel } = progress;
    useEffect(() => {
        const visible: string[] = JSON.parse(visibleProgress);
        previousVisible.current
            .filter((identifier) => !visible.includes(identifier))
            .forEach(cancel);
        visible.forEach((identifier) => load(identifier));
        previousVisible.current = visible;
    }, [visibleProgress, load, cancel]);
    const refreshView = () => {
        refresh();
        (JSON.parse(visibleProgress) as string[]).forEach((identifier) =>
            load(identifier, true)
        );
    };

    const updateQuery = useCallback(
        (updates: Partial<SreInflightSearchQuery>) => {
            params.setParams({
                [`${prefix}Search`]:
                    'search' in updates ? updates.search : query.search,
                [`${prefix}Id`]:
                    'uniqueIdentifier' in updates
                        ? updates.uniqueIdentifier
                        : query.uniqueIdentifier,
                [`${prefix}Stage`]:
                    'stage' in updates ? updates.stage : query.stage,
                [`${prefix}State`]:
                    'stageState' in updates
                        ? updates.stageState
                        : query.stageState,
                [`${prefix}Type`]:
                    'orchestrationType' in updates
                        ? updates.orchestrationType
                        : query.orchestrationType,
                [`${prefix}Pipeline`]:
                    'pipeline' in updates ? updates.pipeline : query.pipeline,
                [`${prefix}Region`]:
                    'region' in updates ? updates.region : query.region,
                [`${prefix}Aged`]:
                    'agedOlderThanHours' in updates
                        ? updates.agedOlderThanHours
                        : query.agedOlderThanHours,
                [`${prefix}Retries`]:
                    'minRetryCount' in updates
                        ? updates.minRetryCount
                        : query.minRetryCount,
                [`${prefix}Sort`]: updates.sortBy ?? query.sortBy,
                [`${prefix}Dir`]: updates.sortDirection ?? query.sortDirection,
                [`${prefix}Page`]: updates.page ?? query.page,
                [`${prefix}Size`]: updates.pageSize ?? query.pageSize,
                sreTab: type,
            });
        },
        [params, prefix, query, type]
    );

    useEffect(() => {
        if (!data || isLoading) return;
        const lastPage = Math.max(1, data.page.totalPages);
        if (data.page.pageNumber > lastPage && query.page !== lastPage) {
            updateQuery({ page: lastPage });
        }
    }, [data, isLoading, query.page, updateQuery]);

    const clearAllFilters = useCallback(() => {
        params.setParams({
            [`${prefix}Search`]: undefined,
            [`${prefix}Id`]: undefined,
            [`${prefix}Stage`]: undefined,
            [`${prefix}State`]: undefined,
            [`${prefix}Type`]: undefined,
            [`${prefix}Pipeline`]: undefined,
            [`${prefix}Region`]: undefined,
            [`${prefix}Aged`]: undefined,
            [`${prefix}Retries`]: undefined,
            [`${prefix}Page`]: 1,
            sreTab: type,
        });
    }, [params, prefix, type]);

    const toggleExpanded = useCallback(
        (uniqueIdentifier: string) => {
            const current = expandedByTab[type] ?? [];
            const isOpen = current.includes(uniqueIdentifier);

            setExpandedByTab({
                ...expandedByTab,
                [type]: isOpen
                    ? current.filter((id) => id !== uniqueIdentifier)
                    : [...current, uniqueIdentifier],
            });
        },
        [expandedByTab, progress, setExpandedByTab, type]
    );

    const onSort = useCallback(
        (field: string) => {
            const isSameField = query.sortBy === field;
            updateQuery({
                sortBy: field,
                sortDirection:
                    isSameField && query.sortDirection === 'asc'
                        ? 'desc'
                        : 'asc',
                page: 1,
            });
        },
        [query.sortBy, query.sortDirection, updateQuery]
    );

    const toggleStageFilter = useCallback(
        (stageNumber: number) => {
            const current = query.stage ?? [];
            const next = current.includes(stageNumber)
                ? current.filter((s) => s !== stageNumber)
                : [...current, stageNumber];
            updateQuery({ stage: next.length ? next : undefined, page: 1 });
        },
        [query.stage, updateQuery]
    );

    const toggleSelected = useCallback((uniqueIdentifier: number) => {
        setSelected((current) =>
            current.includes(uniqueIdentifier)
                ? current.filter((id) => id !== uniqueIdentifier)
                : [...current, uniqueIdentifier]
        );
    }, []);

    const toggleSelectAll = useCallback(
        (uniqueIdentifiers: number[], select: boolean) => {
            setSelected((current) =>
                select
                    ? Array.from(new Set([...current, ...uniqueIdentifiers]))
                    : current.filter((id) => !uniqueIdentifiers.includes(id))
            );
        },
        []
    );

    const exportSelected = useCallback(() => {
        const chosen = (data?.items ?? []).filter((item) =>
            selected.includes(item.id)
        );

        if (chosen.length === 0) {
            return;
        }

        downloadCsv(inflightCsvFileName(type), buildInflightCsv(type, chosen));
    }, [data, selected, type]);

    const exportPage = useCallback(() => {
        const items = data?.items ?? [];

        if (items.length === 0) {
            return;
        }

        downloadCsv(inflightCsvFileName(type), buildInflightCsv(type, items));
    }, [data, type]);

    const findings = useFindingsExport(type, query);
    const chosenItems = (data?.items ?? []).filter((item) =>
        selected.includes(item.id)
    );

    // Any change to the query - filter, sort, page or tab - invalidates the selection, because only
    // the current page is ever in memory and exporting a row the user can no longer see would be a
    // surprise. Compared by value; the query object is rebuilt each render.
    const querySignature = JSON.stringify(query);
    useEffect(() => {
        setSelected([]);
    }, [querySignature, type]);

    if (!access.canCallApi) {
        return (
            <MessageBar messageBarType={MessageBarType.info}>
                {access.reason}
            </MessageBar>
        );
    }

    const orchestrationTypes =
        data?.facets.byOrchestrationType.map((b) => b.name).filter(Boolean) ??
        [];

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { minWidth: 0 } }}>
            {data?.degraded && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    This SRE deployment does not have the paged search endpoint
                    yet, so results were filtered in the browser from the full
                    in-flight snapshot. Stage numbers and stage filtering are
                    unavailable until SRE is updated.
                </MessageBar>
            )}

            <form
                aria-label="Direct identifier lookup"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (lookupId.trim()) setDetailsId(lookupId.trim());
                }}
            >
                <Stack
                    horizontal
                    wrap
                    verticalAlign="end"
                    horizontalAlign="space-between"
                    tokens={{ childrenGap: 8 }}
                >
                    <TextField
                        label="Open build or deployment by identifier"
                        value={lookupId}
                        onChange={(_, value) => setLookupId(value ?? '')}
                        styles={{ root: { flex: '1 1 240px', minWidth: 0 } }}
                    />
                    <DefaultButton
                        type="submit"
                        text="Open details"
                        iconProps={{ iconName: 'OpenPane' }}
                        disabled={!lookupId.trim()}
                    />
                </Stack>
            </form>

            <Stack
                horizontal
                wrap
                verticalAlign="center"
                horizontalAlign="space-between"
                tokens={{ childrenGap: 8 }}
            >
                <Pivot
                    selectedKey={type}
                    onLinkClick={(item) =>
                        item?.props.itemKey &&
                        params.setParams({
                            sreTab: item.props.itemKey,
                            sreDetail: undefined,
                        })
                    }
                >
                    {TABS.map((tab) => (
                        <PivotItem
                            key={tab.key}
                            itemKey={tab.key}
                            headerText={tab.text}
                        />
                    ))}
                </Pivot>
                <div
                    role="group"
                    aria-label="View actions"
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        maxWidth: '100%',
                        minWidth: 0,
                    }}
                >
                    <DefaultButton
                        iconProps={{ iconName: 'Refresh' }}
                        text="Refresh"
                        onClick={refreshView}
                        disabled={isLoading}
                    />
                    <Stack
                        styles={{
                            root: {
                                minWidth: 0,
                                maxWidth: '100%',
                                selectors: {
                                    '> .ms-Stack': {
                                        flexWrap: 'wrap',
                                        rowGap: '4px',
                                    },
                                },
                            },
                        }}
                    >
                        <AutoRefresh onRefresh={refreshView} />
                    </Stack>
                    <DefaultButton
                        iconProps={{ iconName: 'Download' }}
                        text="Export"
                        ariaLabel="Export"
                        menuProps={{
                            items: [
                                {
                                    key: 'exportPage',
                                    text: 'Export this page (CSV)',
                                    iconProps: { iconName: 'Download' },
                                    disabled: isLoading || !data?.items.length,
                                    onClick: () => exportPage(),
                                },
                                // Only builds have validation stages, so only builds can have
                                // findings. Offering this on deployments would return an empty file
                                // that reads as "nothing wrong".
                                ...(supportsFindings(type)
                                    ? [
                                          {
                                              key: 'exportUniqueFindings',
                                              text: 'Export unique files and hashes (CSV)',
                                              iconProps: {
                                                  iconName: 'DownloadDocument',
                                              },
                                              disabled: findings.busy,
                                              onClick: () => {
                                                  void findings.run(
                                                      false,
                                                      'uniqueEntries'
                                                  );
                                              },
                                          },
                                          {
                                              key: 'exportFindings',
                                              text: findings.busy
                                                  ? 'Exporting findings…'
                                                  : 'Export all validation findings (CSV)',
                                              iconProps: {
                                                  iconName: 'DownloadDocument',
                                              },
                                              disabled: findings.busy,
                                              onClick: () => {
                                                  void findings.run();
                                              },
                                          },
                                      ]
                                    : []),
                            ],
                        }}
                    />
                    <CopyButton
                        label="link to this view"
                        value={window.location.href}
                        size="medium"
                    />
                </div>
            </Stack>

            <Stack
                horizontal
                wrap
                verticalAlign="center"
                horizontalAlign="space-between"
                tokens={{ childrenGap: 8 }}
            >
                <SreTriageFilters
                    type={type}
                    query={query}
                    stageCatalog={data?.stageCatalog ?? []}
                    orchestrationTypes={orchestrationTypes}
                    theme={theme}
                    onChange={updateQuery}
                    onClearAll={clearAllFilters}
                />
                <Stack
                    horizontal
                    wrap
                    verticalAlign="center"
                    tokens={{ childrenGap: 12 }}
                    styles={{ root: { minWidth: 0, maxWidth: '100%' } }}
                >
                    {isLoading && <Spinner size={SpinnerSize.small} />}
                    <Text
                        variant="small"
                        styles={{
                            root: { color: theme.palette.neutralSecondary },
                        }}
                    >
                        Updated {formatRelativeToNow(lastUpdated)}
                    </Text>
                    <details style={{ minWidth: 0, maxWidth: '100%' }}>
                        <summary style={{ cursor: 'pointer' }}>
                            SRE connection
                        </summary>
                        <Text styles={{ root: { overflowWrap: 'anywhere' } }}>
                            {access.agrmUrl} · {access.cloud} ·{' '}
                            {access.environment}
                        </Text>
                    </details>
                </Stack>
            </Stack>

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {error}
                </MessageBar>
            )}

            {findings.busy && (
                <Stack
                    horizontal
                    wrap
                    verticalAlign="center"
                    horizontalAlign="end"
                    tokens={{ childrenGap: 8 }}
                >
                    <Spinner label="Exporting findings..." />
                    <DefaultButton
                        text="Cancel export"
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={findings.cancel}
                    />
                </Stack>
            )}
            {findings.message && (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    onDismiss={findings.dismiss}
                    actions={
                        findings.hasMore ? (
                            <DefaultButton
                                text="Export next part"
                                iconProps={{ iconName: 'Download' }}
                                disabled={findings.busy}
                                onClick={() => {
                                    void findings.run(true);
                                }}
                            />
                        ) : undefined
                    }
                >
                    {findings.message}
                </MessageBar>
            )}

            {data && (
                <SreTriageSummary
                    summary={data.summary}
                    theme={theme}
                    hasActiveFilter={hasActiveFilters(query)}
                    activeAgeFilterHours={query.agedOlderThanHours}
                    onFilterByAge={(hours) =>
                        updateQuery({ agedOlderThanHours: hours, page: 1 })
                    }
                />
            )}

            {data && (
                <SreStageBreakdown
                    facets={data.facets}
                    metrics={data.metrics}
                    metricsScope={data.metricsScope}
                    theme={theme}
                    isOpen={breakdownOpen}
                    activeStages={query.stage}
                    onToggleOpen={() => setBreakdownOpen(!breakdownOpen)}
                    onToggleStage={toggleStageFilter}
                />
            )}

            {chosenItems.length > 0 && (
                <Stack
                    horizontal
                    wrap
                    role="group"
                    aria-label="Selected row actions"
                    verticalAlign="center"
                    horizontalAlign="space-between"
                    tokens={{ childrenGap: 12 }}
                    styles={{
                        root: {
                            padding: '8px 12px',
                            minWidth: 0,
                            borderTop: `1px solid ${theme.palette.neutralLight}`,
                            borderBottom: `1px solid ${theme.palette.neutralLight}`,
                            backgroundColor: theme.palette.themeLighterAlt,
                        },
                    }}
                >
                    <Stack
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 12 }}
                    >
                        <Text
                            variant="small"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            {chosenItems.length}{' '}
                            {chosenItems.length === 1 ? 'row' : 'rows'} selected
                        </Text>
                    </Stack>

                    <Stack
                        horizontal
                        wrap
                        verticalAlign="center"
                        tokens={{ childrenGap: 8 }}
                        styles={{
                            root: {
                                marginLeft: 'auto',
                                minWidth: 0,
                                maxWidth: '100%',
                            },
                        }}
                    >
                        <CopyButton
                            label="selected identifiers"
                            size="medium"
                            value={Array.from(
                                new Set(
                                    chosenItems.map(
                                        (item) => item.uniqueIdentifier
                                    )
                                )
                            ).join('\n')}
                        />
                        <DefaultButton
                            iconProps={{ iconName: 'Download' }}
                            text="Export selected (CSV)"
                            onClick={exportSelected}
                            disabled={isLoading}
                        />
                        <TooltipHost content="Clear selection">
                            <IconButton
                                iconProps={{ iconName: 'ClearSelection' }}
                                title="Clear selection"
                                ariaLabel="Clear selection"
                                onClick={() => setSelected([])}
                                styles={{ root: { width: 32, height: 32 } }}
                            />
                        </TooltipHost>
                    </Stack>
                </Stack>
            )}

            <SreTriageList
                type={type}
                result={data}
                isLoading={isLoading}
                theme={theme}
                expanded={expanded}
                selected={selected}
                progress={progress}
                sortBy={query.sortBy ?? defaultSortFor(type)}
                sortDirection={query.sortDirection ?? 'asc'}
                onToggleExpanded={toggleExpanded}
                onOpenDetails={setDetailsId}
                onToggleSelected={toggleSelected}
                onToggleSelectAll={toggleSelectAll}
                onSort={onSort}
                onPageChange={(page) => updateQuery({ page })}
                onPageSizeChange={(pageSize) =>
                    updateQuery({ pageSize, page: 1 })
                }
            />

            <SreTriageDetailPanel
                item={detailsItem}
                identifier={detailsId}
                theme={theme}
                progress={progress}
                onDismiss={() => setDetailsId(null)}
            />
        </Stack>
    );
};
